// One person in the plan.
//
// This list says who the plan has identified and how far each of them has got — not a
// name and one fact but a name and a state, because the panel this row sits in is the
// plan's detail view: what has been decided and in what state, and who the plan concerns.
// It is not an audience list, so somebody the facilitator has merely heard of belongs on
// it too, and nobody is removed by this row deciding how to describe them.
//
// No role — "users do not think in terms of principal and interested party, and the
// interface does not present them" — and there is no role in this component to present,
// because `decodePerson` drops the field before it becomes a value anywhere in the browser.
//
// Three states, not two: `person.joined` renders nothing, same as always; `person.invited`
// with no account yet renders "hasn’t joined yet"; neither renders "not invited". The two
// strings say different things and neither carries a "yet" it hasn't earned. An invited
// person is expected — a principal decided to write to them, so "hasn’t joined yet" says
// they will read this thread the day they do. An identified person may never be invited at
// all — the plan may have only just heard their name, or a principal may have decided
// against involving them — so "not invited" claims nothing about what happens next.
//
// The reader's own row says "You", not their own name read back to them — the one row on
// this list where the name is redundant with who is looking at it.
//
// The address sits under the name, on its own line, because that is the whole reason to
// show it at all (issue #138): a principal has to be able to check an address before
// approving something that sends to it, and prose recalled from the conversation cannot
// be checked. `person.email` arrives real only for a reader who currently holds a
// principal's grant and null for everybody else (`contract.ts`'s `Person.email`), so
// this line renders for a principal reading and says nothing at all — not even an empty
// line — for anyone else, or for anyone with no address on file yet.

import type { Person } from '../api/contract.js';

export type PersonRowProps = {
  person: Person;
};

export function PersonRow({ person }: PersonRowProps): JSX.Element {
  return (
    <li className={person.self ? 'person person--self' : 'person'}>
      <span className="person__identity">
        <span className="person__name">{person.self ? 'You' : person.name}</span>
        {person.email === null ? null : (
          <span className="person__email">{person.email}</span>
        )}
      </span>
      {person.joined ? null : (
        <span className="person__status">
          {person.invited ? 'hasn’t joined yet' : 'not invited'}
        </span>
      )}
    </li>
  );
}
